import { useEffect, useRef, useState } from 'react'
import { formatDuration } from '../utils/date'

interface TimerProps {
  seconds: number
  title?: string
  onDone: () => void
  haptics?: boolean
}

export function Timer({ seconds, title = 'Таймер', onDone, haptics = true }: TimerProps) {
  const [remaining, setRemaining] = useState(seconds)
  const [running, setRunning] = useState(false)
  const doneRef = useRef(false)
  const endAtRef = useRef<number | undefined>(undefined)
  const onDoneRef = useRef(onDone)

  useEffect(() => { onDoneRef.current = onDone }, [onDone])
  useEffect(() => { setRemaining(seconds); setRunning(false); endAtRef.current = undefined; doneRef.current = false }, [seconds])
  useEffect(() => {
    if (!running) return
    const tick = () => {
      const next = Math.max(0, Math.ceil(((endAtRef.current ?? Date.now()) - Date.now()) / 1000))
      setRemaining(next)
      if (next > 0 || doneRef.current) return
      doneRef.current = true
      setRunning(false)
      if (haptics && 'vibrate' in navigator) navigator.vibrate([80, 40, 80])
      onDoneRef.current()
    }
    tick()
    const timer = window.setInterval(tick, 250)
    document.addEventListener('visibilitychange', tick)
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', tick) }
  }, [haptics, running])

  const toggle = () => {
    if (running) {
      setRemaining(Math.max(0, Math.ceil(((endAtRef.current ?? Date.now()) - Date.now()) / 1000)))
      setRunning(false)
      return
    }
    if (remaining <= 0) return
    endAtRef.current = Date.now() + remaining * 1000
    setRunning(true)
  }

  return (
    <section className="timer" aria-label={title}>
      <p className="eyebrow">{title}</p>
      <div className="timer__digits" aria-live="polite">{formatDuration(remaining)}</div>
      <div className="timer__actions">
        <button className="button button--secondary" onClick={toggle}>{running ? 'Пауза' : 'Старт'}</button>
        <button className="button button--ghost" onClick={() => { if (doneRef.current) return; doneRef.current = true; onDone() }}>Пропустить</button>
      </div>
    </section>
  )
}
